/**
 * CollapsibleSection - Individual collapsible section for AI chat responses
 * Features animated expand/collapse with color-coded headers
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { MarkdownSection, getSectionStyles, getFriendlyTitle } from '../../utils/markdownSectionParser';

interface CollapsibleSectionProps {
  section: MarkdownSection;
  isExpanded: boolean;
  onToggle: () => void;
  // Pass through MarkdownRenderer props
  onImageClick?: (src: string) => void;
  onPageLinkClick?: (bookId: string, pageNumber: number) => void;
  onNoteClick?: (noteId: string) => void;
  onTimestampClick?: (sec: number) => void;
  className?: string;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  section,
  isExpanded,
  onToggle,
  onImageClick,
  onPageLinkClick,
  onNoteClick,
  onTimestampClick,
  className = ''
}) => {
  const styles = getSectionStyles(section.title);

  return (
    <div className={`w-full ${className}`}>
      {/* Section Header - Clickable */}
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className={`
          w-full flex items-center justify-between
          px-3 py-2 rounded-lg
          ${styles.headerBg} ${styles.headerBorder}
          border
          hover:opacity-90 transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-cyan-500/30
          text-left
          ${styles.highlight ? 'relative overflow-hidden border-2' : ''}
        `}
        aria-expanded={isExpanded}
        aria-controls={`section-content-${section.id}`}
        {...(styles.highlight && !isExpanded ? {
          animate: {
            boxShadow: [
              '0 0 4px rgba(168, 85, 247, 0.2)',
              '0 0 14px rgba(168, 85, 247, 0.45)',
              '0 0 4px rgba(168, 85, 247, 0.2)',
            ],
          },
          transition: {
            duration: 3,
            ease: 'easeInOut',
            times: [0, 0.5, 1],
            repeat: Infinity,
          },
        } : {})}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-base flex-shrink-0">{section.emoji}</span>
          <span className={`text-sm font-semibold ${styles.headerText} truncate`}>
            {getFriendlyTitle(section.title)}
          </span>
        </div>

        <motion.div
          animate={{ rotate: isExpanded ? 90 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 ml-2"
        >
          <ChevronRightIcon className={`h-4 w-4 ${styles.headerText}`} />
        </motion.div>
      </motion.button>

      {/* Section Content - Animated */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            id={`section-content-${section.id}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="pt-3 px-1">
              <MarkdownRenderer
                content={section.content}
                className="text-sm text-slate-800 dark:text-[#E2E8F0]"
                onImageClick={onImageClick}
                onPageLinkClick={onPageLinkClick}
                onNoteClick={onNoteClick}
                onTimestampClick={onTimestampClick}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CollapsibleSection;
